'use client'
import { useLang } from '../../context/LangContext'
import { t, productSentry } from '../../lib/translations'
import ProductLanding from '../../components/ProductLanding'
import Icon from '../../components/Icon'

const featureIcons = ['bell', 'chart-bar', 'server', 'apps', 'lightning', 'globe', 'check-circle', 'users'] as const

export default function SentryPage() {
  const lang = useLang()
  const features = productSentry.features.map((f, i) => ({
    title: t(lang, f.title),
    description: t(lang, f.description),
    icon: <Icon name={featureIcons[i % featureIcons.length]} className="w-6 h-6" strokeWidth={1.5} />,
  }))
  const benefits = productSentry.benefits.map((b) => ({ title: t(lang, b.title), desc: t(lang, b.desc) }))

  return (
    <ProductLanding
      id="sentry"
      title={t(lang, productSentry.title)}
      subtitle={t(lang, productSentry.subtitle)}
      description={t(lang, productSentry.description)}
      heroBg="bg-gradient-to-b from-[#eab308]/5 via-transparent to-transparent"
      heroGlow="bg-[#facc15]/10"
      features={features}
      benefits={benefits}
    >
      <section className="relative py-24 sm:py-32 overflow-hidden" data-tag="sentry-infrastructure">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div
          className="absolute top-1/3 left-0 w-96 h-96 rounded-full blur-[160px] pointer-events-none opacity-20"
          style={{ background: 'radial-gradient(circle, #eab308, transparent)' }}
        />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <div className="text-center mb-16">
            <Icon name="sentry" className="w-16 h-16 text-[#eab308] mx-auto mb-6 drop-shadow-lg" strokeWidth={1.5} />
            <h2 className="text-3xl font-bold text-white mb-4">
              {lang === 'fa' ? 'پلن‌های سنتری' : lang === 'ar' ? 'بنية سنتري' : 'Sentry Infrastructure'}
            </h2>
            <p className="text-[#94a3b8] max-w-2xl mx-auto">
              {lang === 'fa'
                ? 'ما سنتری را برای نیازهای مانیتورینگ خطای شما فراهم و مدیریت می‌کنیم.'
                : lang === 'ar'
                ? 'نحن نوفر وندير سنتري لتلبية احتياجات مراقبة الأخطاء الخاصة بك.'
                : 'We provide and maintain Sentry for your error monitoring needs.'}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="rounded-xl border border-[#eab308]/25 bg-gradient-to-b from-[#eab308]/8 to-transparent p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="user" className="w-6 h-6 text-[#eab308]" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-white">
                      {lang === 'fa' ? 'برای توسعه‌دهندگان' : lang === 'ar' ? 'للمطورين' : 'For Developers'}
                    </h3>
                  </div>
                  <p className="text-sm text-[#94a3b8] mb-6">
                    {lang === 'fa'
                      ? 'یک نمونه اشتراکی سنتری با محدودیت منصفانه و قیمت ثابت.'
                      : lang === 'ar'
                      ? 'نسخة سنتري مشتركة بحدود استخدام عادلة وسعر ثابت.'
                      : 'A shared Sentry instance with fair usage limits and fixed pricing.'}
                  </p>
                  <ul className="space-y-3">
                    {[
                      { en: 'Managed by us', fa: 'مدیریت شده توسط ما', ar: 'مُدار من قبلنا' },
                      { en: 'Fair usage limit', fa: 'محدودیت منصفانه استفاده', ar: 'حد الاستخدام العادل' },
                      { en: 'Fixed price', fa: 'قیمت ثابت', ar: 'سعر ثابت' },
                      { en: 'Instant setup', fa: 'راه‌اندازی فوری', ar: 'إعداد فوري' },
                    ].map((item, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-[#e2e8f0]">
                        <Icon name="check-circle" className="w-4 h-4 text-[#eab308]" strokeWidth={1.5} />
                        {lang === 'fa' ? item.fa : lang === 'ar' ? item.ar : item.en}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-xl border border-[#eab308]/15 bg-gradient-to-b from-[#eab308]/4 to-transparent p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="users" className="w-6 h-6 text-[#eab308]" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-white">
                      {lang === 'fa' ? 'برای استارت‌آپ‌ها' : lang === 'ar' ? 'للشركات الناشئة' : 'For Startups'}
                    </h3>
                  </div>
                  <p className="text-sm text-[#94a3b8] mb-6">
                    {lang === 'fa'
                      ? 'یک نمونه اختصاصی سنتری که با تیم شما مقیاس می‌شود.'
                      : lang === 'ar'
                      ? 'نسخة سنتري مخصصة تتوسع مع فريقك.'
                      : 'A dedicated Sentry instance that scales with your team.'}
                  </p>
                  <ul className="space-y-3">
                    {[
                      { en: 'Managed by us', fa: 'مدیریت شده توسط ما', ar: 'مُدار من قبلنا' },
                      { en: 'Fully scalable', fa: 'کاملاً مقیاس‌پذیر', ar: 'قابل للتوسع بالكامل' },
                      { en: 'Unlimited usage', fa: 'استفاده نامحدود', ar: 'استخدام غير محدود' },
                      { en: 'Dedicated resources', fa: 'منابع اختصاصی', ar: 'موارد مخصصة' },
                    ].map((item, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-[#e2e8f0]">
                        <Icon name="check-circle" className="w-4 h-4 text-[#eab308]" strokeWidth={1.5} />
                        {lang === 'fa' ? item.fa : lang === 'ar' ? item.ar : item.en}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10 text-center">
                <a
                  href={`/${lang}/contact`}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#eab308] to-[#facc15] hover:from-[#facc15] hover:to-[#eab308] text-[#0f172a] text-sm font-semibold btn-sentry shadow-lg shadow-[#eab308]/20"
                >
                  {lang === 'fa' ? 'تماس با پشتیبانی' : lang === 'ar' ? 'اتصل بالدعم' : 'Contact Us'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ProductLanding>
  )
}
